import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { ColumnDef } from "@tanstack/react-table"
import HrReportTemplate from "Components/templates/hr/hr-report"
import BarChartCard from "Components/organisms/hr-reports/bar-chart-card"
import EmployeeLink from "Components/organisms/hr-reports/employee-link"
import EmployeeTable from "Components/organisms/hr-reports/employee-table"
import FilterBar from "Components/organisms/hr-reports/filter-bar"
import StatCard from "Components/organisms/hr-reports/stat-card"
import useCrossFilter from "hooks/useCrossFilter"
import { COOP_EMPLOYEE_TYPE, HrReportRow } from "models/hr-reports"
import { HrReportsSliceState, setDemographicsReport } from "slices/human-resources/hr-reports/reducer"
import { getDemographicsReport } from "slices/human-resources/hr-reports/thunk"
import { groupByDepartment, HR_DIM, HR_TOP_FILTERS, hrReportDimensions, isCoop } from "utils/hr-reports"

const selectHrReports = createSelector(
    (state: any) => state.HrReports,
    ({ demographics }: HrReportsSliceState) => ({ demographics })
)

// Charts in display order (Headcount by Department sits above them, full width)
const CHARTS = [
    { key: HR_DIM.positionType, title: "Position Type" },
    { key: HR_DIM.designation, title: "Designation" },
    { key: HR_DIM.officeLocation, title: "Office Location" },
    { key: HR_DIM.gender, title: "Gender" },
    { key: HR_DIM.indigenous, title: "Indigenous" },
    { key: HR_DIM.esa, title: "ESA Exempt" },
    { key: HR_DIM.identity, title: "Identity" },
]

const DEPARTMENT_STACKS = [
    { dataKey: "employees", label: "Employees", className: "" },
    { dataKey: "coops", label: "Co-ops", className: "is-secondary" },
]

const text = (value: string | null) => value ?? "-"

const COLUMNS: ColumnDef<HrReportRow, any>[] = [
    { accessorKey: "name", header: "Employee", size: 200, cell: ({ row }) => <EmployeeLink id={row.original.id} name={row.original.name} /> },
    { accessorKey: "email", header: "Email", size: 240, cell: ({ getValue }) => text(getValue()) },
    { accessorKey: "jobTitle", header: "Job Title", size: 220, cell: ({ getValue }) => text(getValue()) },
    { accessorKey: "employeeType", header: "Employee Type", size: 140, cell: ({ getValue }) => text(getValue()) },
    { accessorKey: "division", header: "Division", size: 180, cell: ({ getValue }) => text(getValue()) },
    { accessorKey: "department", header: "Department", size: 200, cell: ({ getValue }) => text(getValue()) },
    { accessorKey: "officeLocation", header: "Office Location", size: 180, cell: ({ getValue }) => text(getValue()) },
    { accessorKey: "manager", header: "Manager", size: 180, cell: ({ row }) => <EmployeeLink id={row.original.managerId} name={row.original.manager} /> },
]

const searchText = (r: HrReportRow) => [r.name, r.email, r.jobTitle, r.division, r.department, r.officeLocation, r.manager].join(" ")

const DemographicsPage = () => {
    document.title = "Demographics | IbexIQ"
    const dispatch: any = useDispatch()
    const { demographics } = useSelector(selectHrReports)
    const [isLoading, setIsLoading] = useState(demographics === null)
    const [hasError, setHasError] = useState(false)

    const load = async () => {
        setIsLoading(true)
        setHasError(false)
        const rows = await getDemographicsReport()
        if (rows) dispatch(setDemographicsReport(rows))
        else setHasError(true)
        setIsLoading(false)
    }

    // Always refresh on visit; cached rows show meanwhile when there are any
    useEffect(() => { load() }, [])

    const dimensions = useMemo(() => hrReportDimensions<HrReportRow>(), [])
    const filter = useCrossFilter<HrReportRow>(demographics ?? [], dimensions)
    const { rowsExcept } = filter

    // Headcount by department, split into employees and co-ops
    const departments = useMemo(() => groupByDepartment(rowsExcept(HR_DIM.department)).map(d => ({
        value: d.department,
        count: d.people.length,
        coops: d.people.filter(isCoop).length,
        employees: d.people.filter(p => !isCoop(p)).length,
    })), [rowsExcept])

    // Stat cards ignore the employee type filter so they keep their numbers while one is picked
    const typeRows = rowsExcept(HR_DIM.employeeType)
    const otherTypes = filter.countsFor(HR_DIM.employeeType).map(c => c.value).filter(v => v !== COOP_EMPLOYEE_TYPE)
    const selectedTypes = filter.selectedFor(HR_DIM.employeeType)
    const isOnly = (values: string[]) => values.length > 0 && selectedTypes.length === values.length && values.every(v => selectedTypes.includes(v))
    const pickTypes = (values: string[]) => filter.select(HR_DIM.employeeType, isOnly(values) ? [] : values)

    return <HrReportTemplate
        title="Demographics"
        manual="human-resources/demographics"
        isLoading={isLoading && demographics === null}
        hasError={hasError && demographics === null}
        onRetry={load}
    >
        <FilterBar filter={filter} dropdowns={HR_TOP_FILTERS} />

        <div className="hr-report-stats">
            <StatCard
                label="Active people"
                value={typeRows.length}
                active={selectedTypes.length === 0}
                onClick={() => filter.clear(HR_DIM.employeeType)}
            />
            <StatCard
                label="Employees"
                value={typeRows.filter(p => !isCoop(p)).length}
                caption="Everyone but co-ops"
                active={isOnly(otherTypes)}
                onClick={() => pickTypes(otherTypes)}
            />
            <StatCard
                label="Co-ops"
                value={typeRows.filter(isCoop).length}
                active={isOnly([COOP_EMPLOYEE_TYPE])}
                onClick={() => pickTypes([COOP_EMPLOYEE_TYPE])}
            />
        </div>

        <div className="hr-report-grid">
            <BarChartCard
                wide
                title="Headcount by Department"
                data={departments}
                stacks={DEPARTMENT_STACKS}
                selected={filter.selectedFor(HR_DIM.department)}
                onSelect={(value, additive) => filter.toggle(HR_DIM.department, value, additive)}
                onClear={() => filter.clear(HR_DIM.department)}
            />
            {CHARTS.map(c => (
                <BarChartCard
                    key={c.key}
                    title={c.title}
                    data={filter.countsFor(c.key)}
                    selected={filter.selectedFor(c.key)}
                    onSelect={(value, additive) => filter.toggle(c.key, value, additive)}
                    onClear={() => filter.clear(c.key)}
                />
            ))}
        </div>

        <EmployeeTable
            title="Employees"
            report="Demographics"
            rows={filter.filtered}
            columns={COLUMNS}
            searchText={searchText}
            initialSorting={[{ id: "name", desc: false }]}
        />
    </HrReportTemplate>
}

export default DemographicsPage
